import './RulesScreen.scss';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionSummary, AccordionDetails, Paper, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Condition from './Condition';
import Result from './Result';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import AddIcon from '@mui/icons-material/Add';
import Button from 'src/component/UI/Button';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { addRuleCondition, deleteRule } from 'src/module/rule/action';
import { getCriticalityLabels, getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor, conditionOptions as defaultConditionOptions } from 'src/utils/utils';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PermissionFence from 'src/component/PermissionFence';
import PagerDutySvg from '../../../assets/icons/pager-duty.svg';
import { getDeviceGroupsLabels } from 'src/module/groupDevices/selector';
import FeatureFence from 'src/component/FeatureFence';
import { useTranslation } from 'src/i18n';

export default function Rule (props) {
  const {
    orderNumber,
    rule,
    hasPagerDutyIntegration
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const deviceGroupsLabels: any = useSelector(getDeviceGroupsLabels);
  const [conditions, setConditions] = React.useState([]);
  const [conditionTypeOptions, setconditionTypeOptions] = React.useState(List([]));
  const [pagerDutyNotificationDisabled, setPagerDutyNotificationDisabled] = React.useState(rule.get('criticality') === 0);
  const temperatureUnit = useSelector(getTemperatureUnit);

  const buildConditionOptions = () => {
    const updatedConditionsOptions: any = [... defaultConditionOptions];
    if (deviceGroupsLabels && deviceGroupsLabels.toJS) {
      const deviceGroupsOptions = deviceGroupsLabels.toJS().map((deviceGroupLabel) => {
        return {
          "value": deviceGroupLabel.id,
          "label": deviceGroupLabel.name,
        };
      });

      if (deviceGroupsOptions.length) {
        const groupedDevicesIndex = updatedConditionsOptions.findIndex(
          (option) => option.label === 'Device Group'
        );
        if (groupedDevicesIndex === -1) {
          const newGroupedDevicesOption = {
            "type": "select",
            "value": "device_groups",
            "conditionType": "eht_alarm",
            label: 'Device Group',
            "compareOptions": [
              "=",
            ],
            options: deviceGroupsOptions,
            "disabled": false
          };
          updatedConditionsOptions.push(newGroupedDevicesOption);
        }
      }
    }
    return updatedConditionsOptions;
  };
  const conditionOptions = buildConditionOptions();


  const loadConditions = () => {
    const conditionsTemp = [];
    const circuitConditions = rule?.getIn(['ruleConditions', 'eht_circuit'], Map());
    const circuitConditionValues = Object.values(circuitConditions && circuitConditions.toJS ? circuitConditions.toJS() : {});
    const circuitRowsKeys = Object.keys(circuitConditions && circuitConditions.toJS ? circuitConditions.toJS() : {});

    const alarmConditions = rule?.getIn(['ruleConditions', 'eht_alarm'], Map());
    const alarmConditionValues = Object.values(alarmConditions && alarmConditions.toJS ? alarmConditions.toJS() : {});
    const alarmRowsKeys = Object.keys(alarmConditions && alarmConditions.toJS ? alarmConditions.toJS() : {});

    circuitConditionValues.map((con: any, index) => {
      conditionsTemp.push({
        ...con,
        label: circuitRowsKeys[index],
      });
    });
    alarmConditionValues.map((con: any, index) => {
      conditionsTemp.push({
        ...con,
        label: alarmRowsKeys[index],
      });
    });
    setConditions(conditionsTemp.sort((a, b) => a.created_at > b.created_at ? 1 : -1));
  };

  useEffect(() => {
    loadConditions();
  }, [rule]);

  useEffect(() => {
    setPagerDutyNotificationDisabled(rule.get('criticality') === 0);

    const labels = conditions.map(x => x.label);
    const filteredOptions = conditionOptions.map((x: any) => {
      return {
        ...x,
        disabled: labels?.includes(x.value)
      };
    });
    setconditionTypeOptions(List(filteredOptions));
  }, [rule, deviceGroupsLabels, conditions]);

  const addCondition = () => {
    const options = conditionOptions;
    const aConditionNotUsedYet = options.find(conOpt => !conditions.some(con => con.label === conOpt.value));
    if (aConditionNotUsedYet) {
      const addRule = {
        object: aConditionNotUsedYet.conditionType,
        property: aConditionNotUsedYet.value,
        value: ''
      };
      dispatch(addRuleCondition(rule.get('id'), Map(addRule)));

    }

    return;
  };
  const criticalityLabels = useSelector(getCriticalityLabels).toJS();
  const getComparisonText = (comparison: string) => {
    const comparisonKeys: Record<string, string> = {
      '=': 'rules.comparison.isEqualTo',
      '<': 'rules.comparison.isLessThan',
      '<=': 'rules.comparison.isLessThanOrEqualTo',
      '>': 'rules.comparison.isGreaterThan',
      '>=': 'rules.comparison.isGreaterThanOrEqualTo',
      '!=': 'rules.comparison.isNotEqualTo'
    };
    return t(comparisonKeys[comparison] || comparisonKeys['=']);
  };

  const getConditionLabel = (label: string) => {
    const labelKeys: Record<string, string> = {
      'Commodity': 'rules.condition.commodity',
      'Location': 'rules.condition.location',
      'Line Size': 'rules.condition.lineSize',
      'Dead Legs': 'rules.condition.deadLegs',
      'Non Functioning': 'rules.condition.nonFunctioning',
      'Alarm': 'rules.condition.alarm',
      'Process Temp': 'rules.condition.processTemp',
      'Ambient Temp': 'rules.condition.ambientTemp',
      'Device Group': 'rules.condition.deviceGroup'
    };
    return labelKeys[label] ? t(labelKeys[label]) : label;
  };

  const titleCreation = () => {
    if (conditions.length === 0) {
      return (<p className='generator'>
        {rule.get('alarm_group', 'None') ? t('rules.alarmGroupIs') : ''}

        {rule.get('alarm_group', 'None') ? < b style={{ marginRight: '5px', marginLeft: '5px' }}>{rule.get('alarm_group', 'None')}</b> : ''}
        {rule.get('alarm_group', 'None') ? t('rules.and') + ' ' : ''}
        {t('rules.criticalityIs')}
        <b style={{ marginRight: '5px', marginLeft: '5px' }}>{criticalityLabels[rule.get('criticality')]}</b>
      </p>
      );
    }
    const conditionsText = conditions.map((condition, index) => {
      const conditionInfo = conditionOptions.find(con => con.value === condition.label);
      let value = conditionInfo?.type === 'select' ? conditionInfo.options.find(x => x.value === condition.value)?.label : condition.value;
      const label = conditionInfo?.label;

      const compare = getComparisonText(condition.comparison);
      if (temperatureUnit === 'F' && conditionInfo.isTemp) {
        value =  celsiusToFahrenheitConvertor(value).toString();
      }
      return (
        <em key={index} className='generatorInner'>{index > 0 ? t('rules.and') + ' ' : ''}{conditionInfo?.conditionType === 'eht_alarm' ? t('rules.anAlarm') : t('rules.aCircuit')} <b>{getConditionLabel(label)}</b> <u>{compare}</u> <b>{value === '' ? `""` : value}</b>
          {condition.or_null ? ' ' + t('rules.or') + ' ' : ''}
          {condition.or_null ? <b>N/A</b> : null}

        </em>
      );
    });

    return (<p className='generator'>
      {t('rules.when')}
      {conditionsText}
      &nbsp;
      {rule.get('alarm_group', 'None') ? t('rules.thenAlarmGroupIs') : ''}

      {rule.get('alarm_group', 'None') ? < b style={{ marginRight: '5px', marginLeft: '5px' }}>{rule.get('alarm_group', 'None')}</b> : ''}
      {rule.get('alarm_group', 'None') ? t('rules.and') + ' ' : ' ' + t('rules.then') + ' '}
      {t('rules.criticalityIs')}
      <b style={{ marginRight: '5px', marginLeft: '5px' }}>{criticalityLabels[rule.get('criticality')]}</b>

    </p>
    );
  };

  const onDelete = (ruleId) => {
    dispatch(deleteRule(ruleId));
  };

  const buildSummary = (showButtons: boolean) => {
    return (
      <table className='ruleTitle'>
        <tbody>
          <tr>
            <td className='ruleNumber'>
              {t('rules.ruleNumber', { number: orderNumber + 1 })}
            </td>
            <td>{titleCreation()}</td>
            <td style={{ width: '80px' }}>
              <div className='ruleTitleButtons'>{showButtons ? (<>
                <Button onClick={() => onDelete(rule.get('id'))} icon={<DeleteIcon className='deleteButton' />} />
                <SwapVertIcon style={{ color: '#444', fontSize: 24, height: '40px', margin: 'auto' }} />
                {rule.get('pager_duty_notification') ?
                  <Tooltip title={t('rules.willNotifyPagerDuty')}>
                    <img src={PagerDutySvg} style={{ height: '16px', width: '12px', marginTop: '13px', marginLeft: '10px' }} />
                  </Tooltip> : null
                }
              </>) : null}
              </div>

            </td>

          </tr>
        </tbody>
      </table>
    );
  };

  return (<>
    <PermissionFence can={['edit-eht-alarm-rule']} noPermissionComponent={(
      <Paper>
        {buildSummary(false)}
      </Paper>
    )}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ flexDirection: "row-reverse" }}
        >
          {buildSummary(true)}
        </AccordionSummary>
        <AccordionDetails>
          <div className='ruleDetails'>
            <div className='inner'>
              <p className='title1'>{t('rules.ifConditionsMet')}</p>
              {conditions.map((condition, index) => {
                const options = conditionOptions.find((option) => option.value === condition.label);
                return (
                  <Condition key={index} condition={condition} ruleId={rule.get('id')} conditionTypeOptions={conditionTypeOptions} conditionOptions={[options]} />
                );
              })}

              <div className='addConditionButton'>
                <Button onClick={() => addCondition()} cta color="primary" variant="contained" icon={<AddIcon style={{ color: 'white', fontSize: 24, marginRight: '25px' }} />} >{t('common.addCondition')}</Button>
              </div>

              <span className='title2'>
                <hr className='line' />
                <p>{t('rules.alarmCategorized')}</p>
              </span>
              {/* <Result /> */}
              <Result label={'criticality'} value={rule.get('criticality')} ruleId={rule.get('id')} />
              <Result label={'alarm_group'} value={rule.get('alarm_group')} ruleId={rule.get('id')} />
              <FeatureFence can={['pager-duty']}>
                {
                  hasPagerDutyIntegration ?
                    <div className='notifications-container'>
                      <span className='title2'>
                        <hr className='line' />
                        <p>{t('rules.notifications')}</p>
                      </span>
                      <Result tooltip={pagerDutyNotificationDisabled ? t('rules.onlyCritical') : ''}  disabled={pagerDutyNotificationDisabled} label={'pager_duty_notification'} value={rule.get('pager_duty_notification')} ruleId={rule.get('id')} />
                    </div> : null
                }
              </FeatureFence>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </PermissionFence>
  </>);
}

Rule.propTypes = {
  orderNumber: PropTypes.number.isRequired,
  rule: ImmutablePropTypes.map,
  hasPagerDutyIntegration: PropTypes.bool
};

Rule.defaultProps = {
};
